import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOperations } from '../context/OperationsContext';

const commands = [
  { label: 'Main dashboard', hint: 'Overview and service health', path: '/', key: 'M' },
  { label: 'Shipments', hint: 'View shipment calculations', path: '/calculations', key: 'S' },
  { label: 'Integrations', hint: 'Manage carrier integrations', path: '/integrations', key: 'I' },
  { label: 'Observability', hint: 'Inspect events and system activity', path: '/observability', key: 'O' },
  { label: 'Pricing rules', hint: 'Configure prices and markups', path: '/pricing_rules', key: 'P' },
  { label: 'API keys', hint: 'Manage API access', path: '/api_keys', key: 'A' },
  { label: 'Clients', hint: 'Manage companies and users', path: '/clients', key: 'C' },
  { label: 'Packaging', hint: 'Manage packaging definitions', path: '/packaging', key: 'B' },
  { label: 'Outgoing webhooks', hint: 'Review webhook deliveries', path: '/outgoing_webhooks', key: 'W' },
  { label: 'Demo', hint: 'Open the delivery widget demo', path: '/demo', key: 'D' },
];

export default function CommandPalette() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { orders } = useOperations();
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const isAdmin = roles.includes('administrator');
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((value) => !value);
      }
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const visibleCommands = commands.filter((command) => command.path !== '/demo' || isAdmin);
  const filtered = visibleCommands.filter((command) => `${command.label} ${command.hint}`.toLowerCase().includes(query.toLowerCase()));
  const shipmentMatches = query.trim().length >= 2 ? (orders || []).filter((order) => {
    const tracking = Array.isArray(order.tracking_numbers)
      ? order.tracking_numbers.filter(Boolean).join(' ')
      : (order.tracking_number || '');
    return [order.order_number, tracking, order.carrier_order_number, order.provider_code].join(' ').toLowerCase().includes(query.trim().toLowerCase());
  }).slice(0, 6) : [];
  const run = (command) => navigate(command.path);

  return (
    <>
      <button type="button" className="command-trigger" onClick={() => setOpen(true)} title="Open command palette (Ctrl+K)">
        <span>Search navigation</span><kbd>Ctrl K</kbd>
      </button>
      {open && <div className="command-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}>
        <section className="command-palette" role="dialog" aria-modal="true" aria-label="Command palette">
          <div className="command-input-wrap">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6" /><path d="m16 16 4 4" /></svg>
            <input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search console..." />
            <kbd>Esc</kbd>
          </div>
          <div className="command-list">
            <p>GO TO</p>
            {filtered.map((command) => <button type="button" key={command.path} onClick={() => run(command)}>
              <span className="command-letter">{command.key}</span><span><strong>{command.label}</strong><small>{command.hint}</small></span><span className="command-arrow">↗</span>
            </button>)}
            {!!shipmentMatches.length && <p>SHIPMENTS</p>}
            {shipmentMatches.map((order) => {
              const tracking = Array.isArray(order.tracking_numbers)
                ? order.tracking_numbers.filter(Boolean).join(', ')
                : (order.tracking_number || '');
              return (
              <button type="button" key={`${order.order_number}-${order.shipment_id}`} onClick={() => navigate('/calculations', { state: { orderNumber: order.order_number } })}>
              <span className="command-letter">#</span><span><strong>{order.order_number}</strong><small>{order.provider_code || 'Unassigned'} · {tracking || 'No tracking yet'}</small></span><span className="command-arrow">↗</span>
            </button>
              );
            })}
            {!filtered.length && !shipmentMatches.length && <div className="command-empty">No matching navigation or shipments</div>}
          </div>
        </section>
      </div>}
    </>
  );
}
